const VIDEO_EXTENSION_RE = /\.(mp4|webm|mov|m4v)(?:[?#].*)?$/i;
const MEDIA_EXTENSION_RE = /\.(jpg|jpeg|png|gif|webp|avif|mp4|webm|mov|m4v)$/i;
const HINT_SLUG_RE = /\/pages-signs\/[^/]+\/([^/.]+)\.html?$/i;
const YOUTUBE_THUMB_RE = /^https:\/\/(?:img\.youtube\.com|i\.ytimg\.com)\//i;

export function isVideoMedia(url: string): boolean {
    return VIDEO_EXTENSION_RE.test(url);
}

export function numberImageUrl(digit: string): string {
    const d = digit.trim();
    if (d === "0") {
        return "/signs/number-0.png";
    }
    return `https://www.lifeprint.com/asl101/signjpegs/numbers/number0${d}.jpg`;
}

export function extractHintSlug(url: string): string | null {
    const match = url.match(HINT_SLUG_RE);
    if (!match) return null;
    return decodeURIComponent(match[1]).toLowerCase();
}

export function normalizeMediaKey(fileName: string): string {
    let key = fileName.toLowerCase();
    key = key.replace(MEDIA_EXTENSION_RE, "");
    key = key.replace(/(?:\.h|\.ht)\d+$/i, "");
    key = key.replace(/-step\d+$/i, "");
    key = key.replace(/([a-z]+-v\d+)[a-z]$/i, "$1");
    key = key.replace(/-0*\d+$/i, "");
    if (!/-v\d+$/i.test(key)) {
        key = key.replace(/(\D)\d+$/i, "$1");
    }
    key = key.replace(/[-_.]+$/g, "");
    return key;
}

export function scoreMediaKey(key: string, slug: string): number {
    if (key === slug) return 3;
    if (key.startsWith(slug) || slug.startsWith(key)) return 2;
    if (key.includes(slug) || slug.includes(key)) return 1;
    return 0;
}

export function pickBestMediaFrames(hintUrl: string, frames: string[]): string[] {
    const cleanFrames = frames.filter(Boolean);
    if (cleanFrames.length <= 1) return cleanFrames;

    const videos = cleanFrames.filter((frame) => isVideoMedia(frame));
    const images = cleanFrames.filter((frame) => !isVideoMedia(frame));
    if (images.length === 0) return videos.slice(0, 1);

    const slug = extractHintSlug(hintUrl);
    if (!slug) return images.slice(0, Math.min(images.length, 8));

    const infos = images.map((url) => {
        const rawName = decodeURIComponent(url.split("/").pop() ?? "");
        return { url, rawName: rawName.toLowerCase(), key: normalizeMediaKey(rawName) };
    });

    const slugMatches = infos.filter((info) => info.rawName.includes(slug));
    const pool = slugMatches.length > 0 ? slugMatches : infos;

    const groups = new Map<string, typeof pool>();
    for (const info of pool) {
        const key = info.key || info.rawName;
        const group = groups.get(key) ?? [];
        group.push(info);
        groups.set(key, group);
    }

    let bestKey: string | null = null;
    let bestGroup: typeof pool = [];
    let bestIsSignImage = false;

    for (const [key, group] of groups) {
        // Every Lifeprint page embeds the same site-wide promo video, so its
        // thumbnail is a candidate on all of them. It is never the sign being
        // looked up, so a group of real sign imagery outranks a group of
        // YouTube stills no matter how the size/slug tiebreaks fall. A page
        // whose only media IS a thumbnail still returns it — nothing beats it.
        const isSignImage = group.some((info) => !YOUTUBE_THUMB_RE.test(info.url));

        if (bestGroup.length === 0) {
            bestKey = key;
            bestGroup = group;
            bestIsSignImage = isSignImage;
            continue;
        }

        if (isSignImage !== bestIsSignImage) {
            if (isSignImage) {
                bestKey = key;
                bestGroup = group;
                bestIsSignImage = isSignImage;
            }
            continue;
        }

        if (group.length > bestGroup.length) {
            bestKey = key;
            bestGroup = group;
            bestIsSignImage = isSignImage;
            continue;
        }

        if (group.length === bestGroup.length && bestKey) {
            if (scoreMediaKey(key, slug) > scoreMediaKey(bestKey, slug)) {
                bestKey = key;
                bestGroup = group;
                bestIsSignImage = isSignImage;
            }
        }
    }

    const selected = bestGroup.length > 0 ? bestGroup : pool;
    selected.sort((a, b) => a.rawName.localeCompare(b.rawName, undefined, { numeric: true }));
    return selected.map((item) => item.url).slice(0, 8);
}
